import { Input } from '@/core/ui/components';
import { Building2 } from 'lucide-react';
import { useEffect } from 'react';
import type { WizardFormData } from '../types';

interface BasicInfoStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  onValidChange: (isValid: boolean) => void;
  errors: Record<string, string>;
}

export function BasicInfoStep({ formData, onChange, onValidChange, errors }: BasicInfoStepProps) {
  useEffect(() => {
    const isValid = formData.name.trim().length > 0 && formData.slug.trim().length > 0;
    onValidChange(isValid);
  }, [formData.name, formData.slug, onValidChange]);

  const handleNameChange = (value: string) => {
    onChange({ name: value });
    // Auto-generate slug from name if slug is empty or was auto-generated
    if (!formData.slug || formData.slug === generateSlug(formData.name)) {
      onChange({ name: value, slug: generateSlug(value) });
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-theme">Basic Information</h3>
          <p className="text-sm text-theme-secondary">Enter the tenant's name and URL identifier</p>
        </div>
      </div>

      <Input
        label="Tenant Name"
        value={formData.name}
        onChange={(e) => handleNameChange(e.target.value)}
        error={errors.name}
        placeholder="Acme Residences"
        required
      />

      <div>
        <Input
          label="URL Slug"
          value={formData.slug}
          onChange={(e) => onChange({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
          error={errors.slug}
          placeholder="acme-residences"
          required
        />
        <p className="text-xs text-theme-tertiary mt-1">
          This will be the tenant's URL: <span className="font-medium">{formData.slug || 'your-slug'}.hommi.mx</span>
        </p>
      </div>
    </div>
  );
}
